import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { euro, dateIt, num } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatoPreventivo } from "./StatoPreventivo";

const Kpi = ({ label, value, testid, cls = "" }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className={`font-mono font-semibold ${cls}`}>{value}</div></div>
);

// Scheda cliente: storico preventivi, totali, conversione, utile dalle commesse chiuse
export function PanoramicaClientePreventivi({ cliente, open, onOpenChange }) {
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ["prev-cliente", cliente?.id], enabled: !!cliente?.id, queryFn: async () => (await api.get(`/preventivi/cliente/${cliente.id}/panoramica`)).data });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="panoramica-cliente-dialog">
        <DialogHeader><DialogTitle className="font-display">Preventivi · {cliente?.ragione_sociale}</DialogTitle><DialogDescription>Storico offerte, conversione e utile generato dalle commesse chiuse nate da preventivi accettati.</DialogDescription></DialogHeader>
        {data && (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Kpi label="Offerto" value={euro(data.totali.offerto)} testid="pan-offerto" />
              <Kpi label="Accettato" value={euro(data.totali.accettato)} testid="pan-accettato" cls="text-emerald-700" />
              <Kpi label="Rifiutato" value={euro(data.totali.rifiutato)} testid="pan-rifiutato" cls="text-red-700" />
              <Kpi label="Conversione" value={`${num(data.tasso_conversione, 1)}%`} testid="pan-conversione" />
              <Kpi label="Utile generato" value={euro(data.utile_generato)} testid="pan-utile" cls={data.utile_generato < 0 ? "text-red-700" : "text-emerald-700"} />
              <Kpi label="Margine medio" value={`${num(data.margine_medio_perc, 1)}%`} testid="pan-margine" />
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm"><thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2">Numero</th><th className="px-3 py-2">Data</th><th className="px-3 py-2">Oggetto</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2 text-right">Imponibile</th></tr></thead>
                <tbody>
                  {data.preventivi.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Nessun preventivo per questo cliente.</td></tr>}
                  {data.preventivi.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer" onClick={() => nav(`/preventivi/${p.id}`)} data-testid={`pan-prev-${p.numero}`}>
                      <td className="px-3 py-2 font-mono text-xs">{p.numero}</td><td className="px-3 py-2">{dateIt(p.data)}</td><td className="px-3 py-2 truncate max-w-[260px]">{p.oggetto}</td><td className="px-3 py-2"><StatoPreventivo stato={p.stato} /></td><td className="px-3 py-2 text-right font-mono">{euro(p.totali?.imponibile)}</td>
                    </tr>
                  ))}
                </tbody></table>
            </div>
            {data.commesse.length > 0 && (
              <div className="text-sm"><div className="font-semibold text-slate-800 mb-1">Commesse chiuse da preventivi</div>
                {data.commesse.map((c) => <div key={c.codice} className="flex justify-between border-b border-slate-100 py-1"><span className="font-mono text-xs">{c.codice}</span><span>Ricavo {euro(c.ricavo)} · Costi {euro(c.costi)} · <b className={c.utile < 0 ? "text-red-700" : "text-emerald-700"}>Utile {euro(c.utile)}</b> ({num(c.margine_perc, 1)}%)</span></div>)}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
